'use strict';

/**
 * Lightbox
 *
 * Opens an image or a fetched+patched SVG in a fullscreen Bootstrap modal on click.
 * Content is dynamically injected and cleared on close.
 *
 * Image usage:
 *   <span role="button" tabindex="0" class="js-lightbox" data-lightbox="path/to/full.jpg">
 *       <img src="path/to/thumbnail.jpg" alt="description" />
 *   </span>
 *
 * Fetch+patch SVG usage (e.g. EU warranty harmonised label):
 *   <span role="button" tabindex="0" class="js-lightbox"
 *       data-lightbox="path/to/blueprint.svg"
 *       data-lightbox-warranty-label='{"brand":"Lorem","ean":"123","years":"2"}'>
 *       <img src="path/to/thumbnail.jpg" />
 *   </span>
 *
 * Optional CTA button (works in both image and fetch+patch SVG mode):
 *   data-lightbox-button='{"url":"https://...","label":"Learn more"}'
 *
 *   - data-lightbox                  URL of the asset to display
 *   - data-lightbox-warranty-label   JSON with brand/ean/years — triggers fetch+patch mode
 *   - data-lightbox-button           JSON with url/label — renders a link button below the content
 *   - alt of the inner <img> is forwarded to the lightbox image (image mode only)
 */

const TRIGGER = '.js-lightbox';
const MODAL_ID = 'lightboxModal';

function injectModal() {
    const modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'modal fade';
    modal.tabIndex = -1;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="bg-white rounded position-fixed z-1 top-0 end-0 m-6">
            <button type="button" class="btn-close p-3" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-dialog modal-xl modal-dialog-centered justify-content-center">
            <div class="modal-content bg-transparent border-0 w-auto">
                <div class="modal-body p-0 text-center position-relative js-lightbox-body"></div>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    return modal;
}

const svgCache = {};

function renderButton(button, $body) {
    if (!button || !button.url || !button.label) {
        return;
    }

    $body.append(
        `<div class="mt-5">
            <a href="${button.url}" target="_blank" rel="noopener noreferrer" class="btn btn-outline-primary btn-lg d-inline-flex align-items-center gap-1">
                ${button.label}
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M5 13h11.17l-4.88 4.88c-.39.39-.39 1.03 0 1.42.39.39 1.02.39 1.41 0l6.59-6.59c.39-.39.39-1.02 0-1.41l-6.58-6.6c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L16.17 11H5c-.55 0-1 .45-1 1s.45 1 1 1z"/></svg>
            </a>
        </div>`
    );
}

function patchAndShow(cleanSvg, values, button, $body, $modal) {
    const svgEl = cleanSvg.cloneNode(true);
    svgEl.style.maxWidth = '100%';
    svgEl.style.height = 'auto';
    svgEl.querySelector('#wl-brand').textContent = values.brand || '';
    svgEl.querySelector('#wl-ean').textContent = values.ean || '';
    svgEl.querySelector('#wl-duration').textContent = values.years || '';
    $body.empty().append(svgEl);
    renderButton(button, $body);
    $modal.modal('show');
}

function fetchAndPatchSvg(src, values, button, $body, $modal) {
    if (svgCache[src]) {
        patchAndShow(svgCache[src], values, button, $body, $modal);
        return;
    }

    fetch(src)
        .then(function (res) {
            return res.text();
        })
        .then(function (svgText) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, 'image/svg+xml');
            const cleanSvg = doc.documentElement;
            cleanSvg.querySelector('#wl-brand').textContent = '';
            cleanSvg.querySelector('#wl-ean').textContent = '';
            cleanSvg.querySelector('#wl-duration').textContent = '';
            svgCache[src] = cleanSvg;
            patchAndShow(svgCache[src], values, button, $body, $modal);
        });
}

module.exports = function () {
    if (!document.querySelector(TRIGGER)) {
        return;
    }

    const $modal = $(injectModal());
    const $body = $modal.find('.js-lightbox-body');

    function openLightbox($el) {
        const src = $el.data('lightbox');
        if (!src) {
            return;
        }

        const button = $el.data('lightbox-button');
        const warrantyLabel = $el.data('lightbox-warranty-label');
        if (warrantyLabel) {
            fetchAndPatchSvg(src, warrantyLabel, button, $body, $modal);
            return;
        }

        const alt = $el.find('img').attr('alt') || $el.attr('alt') || '';
        $body.html(`<img src="${src}" alt="${alt}" class="img-fluid" />`);
        renderButton(button, $body);
        $modal.modal('show');
    }

    $(document).on('mousedown', TRIGGER, function (e) {
        e.preventDefault();
    });

    $(document).on('click', TRIGGER, function () {
        openLightbox($(this));
    });

    $(document).on('keydown', TRIGGER, function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openLightbox($(this));
        }
    });

    $modal.on('hidden.bs.modal', function () {
        $body.empty();
    });
};
